/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SnackBarService } from '../../../../services/snack-bar.service';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';

@Component({
  selector: 'app-change-password-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    TranslateModule
  ],
  templateUrl: './change-password-dialog.component.html',
  styleUrl: './change-password-dialog.component.scss'
})
export class ChangePasswordDialogComponent {
  form: FormGroup;
  isLoading = false;
  showPassword = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    private authService: AuthService,
    private snackBarService: SnackBarService,
    private translate: TranslateService
  ) {
    this.form = this.fb.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(8)]]
    }, { validators: this.passwordMatchValidator });
  }

  private passwordMatchValidator(group: AbstractControl) {
    const newPassword = group.get('newPassword')?.value;
    const confirmControl = group.get('confirmPassword');
    const confirmPassword = confirmControl?.value;

    if (newPassword !== confirmPassword) {
      confirmControl?.setErrors({ ...confirmControl.errors, mismatch: true });
      return { mismatch: true };
    } else {
      if (confirmControl?.hasError('mismatch')) {
        const { mismatch, ...otherErrors } = confirmControl.errors || {};
        const hasOtherErrors = Object.keys(otherErrors).length > 0;
        confirmControl.setErrors(hasOtherErrors ? otherErrors : null);
      }
      return null;
    }
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const { oldPassword, newPassword, confirmPassword } = this.form.value;

    const payload = {
      oldPassword,
      newPassword,
      confirmPassword
    };

    this.authService.changePassword(payload)
      .pipe(
        tap(() => {
          this.isLoading = false;
          this.snackBarService.openSnackBar(
            this.translate.instant('changePassword.success'),
            this.translate.instant('openSnackBar.close')
          );
          this.dialogRef.close();
        }),
        catchError((err) => {
          console.error(err.error.modelState);
          this.isLoading = false;
          this.snackBarService.errorSnackBar(
            err.error?.message || 'Error',
            this.translate.instant('openSnackBar.close')
          );
          return of(null);
        })
      )
      .subscribe();
  }

  close() {
    this.dialogRef.close();
  }
}
